import { Badge } from "@bcc/components/ui/badge";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@bcc/components/ui/card";
import { Label } from "@bcc/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@bcc/components/ui/select";
import { Shield, User } from "lucide-react";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import {
  setRoleSelectorContainerTestId,
  setRoleSelectorCurrentRoleLabelTestId,
  setRoleSelectorDescriptionTestId,
  setRoleSelectorTitleTestId,
} from "./role-selector.utils";

type FinanceRole = "roaster" | "finance-ops";

interface RoleSelectorProps {
  value?: FinanceRole;
  onChange?: (role: FinanceRole) => void;
  testId?: string;
}

export function RoleSelector({ value, onChange, testId }: RoleSelectorProps) {
  const { t } = useTranslation();
  const [selectedRole, setSelectedRole] = useState<FinanceRole>(value || "roaster");

  const handleRoleChange = (role: FinanceRole) => {
    setSelectedRole(role);
    onChange?.(role);
  };

  const roles = [
    {
      value: "roaster" as FinanceRole,
      label: t("finance.roleSelector.roles.roaster.label"),
      description: t("finance.roleSelector.roles.roaster.description"),
      icon: User,
      restrictions: [
        t("finance.roleSelector.roles.roaster.restrictions.noPaymentStatus"),
        t("finance.roleSelector.roles.roaster.restrictions.noAuditLog"),
        t("finance.roleSelector.roles.roaster.restrictions.cannotModifySettings"),
      ],
    },
    {
      value: "finance-ops" as FinanceRole,
      label: t("finance.roleSelector.roles.financeOps.label"),
      description: t("finance.roleSelector.roles.financeOps.description"),
      icon: Shield,
      restrictions: [],
    },
  ];

  const currentRole = roles.find((r) => r.value === selectedRole);

  return (
    <Card testId={testId ?? setRoleSelectorContainerTestId()}>
      <CardHeader>
        <CardTitle data-testid={setRoleSelectorTitleTestId()}>{t("finance.roleSelector.title")}</CardTitle>
        <CardDescription data-testid={setRoleSelectorDescriptionTestId()}>{t("finance.roleSelector.description")}</CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="space-y-2">
          <Label htmlFor="role-select" data-testid={setRoleSelectorCurrentRoleLabelTestId()}>
            {t("finance.roleSelector.currentRole")}
          </Label>
          <Select value={selectedRole} onValueChange={handleRoleChange}>
            <SelectTrigger id="role-select">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {roles.map((role) => (
                <SelectItem key={role.value} value={role.value}>
                  <div className="flex items-center gap-2">
                    <role.icon className="h-4 w-4" />
                    {role.label}
                  </div>
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        {currentRole && (
          <div className="rounded-lg bg-muted p-4 space-y-3">
            <div className="flex items-center gap-2">
              <currentRole.icon className="h-5 w-5 text-muted-foreground" />
              <div>
                <p className="font-medium">{currentRole.label}</p>
                <p className="text-sm text-muted-foreground">{currentRole.description}</p>
              </div>
            </div>

            {currentRole.restrictions.length > 0 && (
              <div className="space-y-2">
                <p className="text-sm font-medium">{t("finance.roleSelector.restrictions")}</p>
                <ul className="space-y-1">
                  {currentRole.restrictions.map((restriction, i) => (
                    <li key={i} className="text-sm text-muted-foreground flex items-center gap-2">
                      <Badge variant="outline" className="shrink-0">
                        {t("finance.roleSelector.limited")}
                      </Badge>
                      {restriction}
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </div>
        )}
      </CardContent>
    </Card>
  );
}
